import { useEffect, useMemo, useState } from 'react';

import { FokParameterInfo, FokParameterType } from '@fora-monorepo/employer-my-pages-common/types';
import { Checkbox, Container, Feedback, Text, useDatePickerInput } from '@foradevelopment/fora-ui';

import { useTypedTranslation } from '../../../i18n/i18n';
import { MarkupAndParametersFormProps } from './types';
import {
  getParameterEndDateMin,
  getParameterStartDateMin,
  getParameterTKeys,
  validateParameterEndDate,
  validateParameterStartDate,
} from '../../../common/utils';
import { useAppSelector } from '../../../redux/hooks';
import { ParameterDateInputFields } from './ParameterDateInputFields';

/** Other parameters than TILLVAL_ASL65 AND TILLVAL_KP65, not used in MVP */
export const ParameterCheckboxOld = ({
  fokParameter,
  setParameterChoicesInput,
  disabled,
  employmentStartDate,
  fokParameterValidity,
  setHasCheckedBox,
  formType,
}: Pick<MarkupAndParametersFormProps, 'setParameterChoicesInput' | 'disabled' | 'formType' | 'employmentStartDate'> & {
  fokParameter: FokParameterInfo;
  employmentStartDate: string;
  fokParameterValidity?: { validTo: string | null; validFrom: string };
  setHasCheckedBox: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTypedTranslation();
  const monthlyReportingReleaseDate = useAppSelector(state => state.foraGlobal.settings.monthlyReportingReleaseDate);

  const [parameterChecked, setParameterChecked] = useState(false);
  const [endDateChecked, setEndDateChecked] = useState(false);

  const startDateInputSettings = useMemo(
    () => ({
      initValue: '',
      validate: validateParameterStartDate(monthlyReportingReleaseDate, employmentStartDate, fokParameterValidity),
      minDate: getParameterStartDateMin(monthlyReportingReleaseDate, employmentStartDate, fokParameterValidity),
    }),
    [monthlyReportingReleaseDate, employmentStartDate, fokParameterValidity]
  );
  const startDateInput = useDatePickerInput(startDateInputSettings);

  const endDateInputSettings = useMemo(
    () => ({
      initValue: '',
      minDate: getParameterEndDateMin(
        fokParameterValidity,
        startDateInput.value,
        formType === 'add' ? employmentStartDate : undefined
      ),
      validate: validateParameterEndDate(fokParameterValidity, startDateInput.value),
    }),
    [employmentStartDate, fokParameterValidity, formType, startDateInput.value]
  );
  const endDateInput = useDatePickerInput(endDateInputSettings);

  useEffect(() => {
    // Update form data state
    setParameterChoicesInput(prevVal => {
      const otherParams = prevVal.filter(
        selected => selected.fokParameter.parameterValue !== fokParameter.parameterValue
      );
      if (!parameterChecked) {
        setHasCheckedBox(otherParams.length > 0 ? true : false);
        return otherParams;
      }

      setHasCheckedBox(true);
      return [
        ...otherParams,
        {
          fokParameter: {
            parameterValue: fokParameter.parameterValue as FokParameterType,
            startDate: startDateInput.value,
            endDate: endDateChecked ? endDateInput.value || undefined : undefined,
          },
          validateInput: () =>
            startDateInput.validateDateInputValue().length === 0 && endDateChecked
              ? endDateInput.validateDateInputValue().length === 0
              : true,
        },
      ];
    });
    // Only update when inputs have new value or box is toggled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterChecked, endDateChecked, startDateInput.value, endDateInput.value]);

  const toggleParameterCheckbox = () => {
    if (parameterChecked) {
      setParameterChecked(false);
      setEndDateChecked(false);
    } else {
      setParameterChecked(true);
    }
  };

  return (
    <Container>
      <Checkbox
        key={fokParameter.parameterValue}
        id={fokParameter.parameterValue}
        checked={parameterChecked}
        onChange={toggleParameterCheckbox}
        disabled={disabled}
      >
        {fokParameter.parameterValue}
      </Checkbox>
      {parameterChecked && (
        <Container style={{ marginLeft: 30, marginTop: 10 }}>
          <Checkbox
            key={fokParameter.parameterValue + '-parameter-end-date-confirmation'}
            id={fokParameter.parameterValue + '-parameter-end-date-confirmation'}
            checked={endDateChecked}
            onChange={() => setEndDateChecked(!endDateChecked)}
            disabled={disabled}
          >
            {t('form.parameter.setEndDate')}
          </Checkbox>
          {endDateChecked && (
            <ParameterDateInputFields
              startDateInputProps={startDateInput.props}
              endDateInputProps={endDateInput.props}
              disabled={disabled}
            />
          )}
        </Container>
      )}
    </Container>
  );
};

/** TILLVAL_ASL65 AND TILLVAL_KP65 */
export const PensionParameterCheckboxOld = ({
  fokParameter,
  setParameterChoicesInput,
  disabled,
  fokParameterValidity,
  setHasCheckedBox,
  employmentStartDate,
  formType,
}: Pick<MarkupAndParametersFormProps, 'setParameterChoicesInput' | 'disabled' | 'formType' | 'employmentStartDate'> & {
  fokParameter: FokParameterInfo;
  fokParameterValidity?: { validTo: string | null; validFrom: string };
  setHasCheckedBox: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTypedTranslation();

  const [parameterChecked, setParameterChecked] = useState(false);
  const [endDateChecked, setEndDateChecked] = useState(false);

  const { parameterValue } = fokParameter;

  const datePickerInputSettings = useMemo(() => {
    return {
      initValue: '',
      minDate: getParameterEndDateMin(
        fokParameterValidity,
        undefined,
        formType === 'add' ? employmentStartDate : undefined
      ),
      maxDate: fokParameterValidity?.validTo,
      validate: validateParameterEndDate(fokParameterValidity, undefined),
    };
  }, [employmentStartDate, fokParameterValidity, formType]);

  const endDateInput = useDatePickerInput(datePickerInputSettings);

  useEffect(() => {
    // Update form data state
    setParameterChoicesInput(prevVal => {
      const otherParams = prevVal.filter(selected => selected.fokParameter.parameterValue !== parameterValue);
      if (!parameterChecked) {
        setHasCheckedBox(otherParams.length > 0 ? true : false);
        return otherParams;
      }

      setHasCheckedBox(true);
      return [
        ...otherParams,
        {
          fokParameter: {
            parameterValue: parameterValue as FokParameterType,
            endDate: endDateChecked ? endDateInput.value || undefined : undefined,
          },
          validateInput: () => (endDateChecked ? endDateInput.validateDateInputValue().length === 0 : true),
        },
      ];
    });
    // Only update when inputs have new value, new validation or box is toggled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterChecked, endDateChecked, endDateInput.value, endDateInput.validateDateInputValue]);

  const toggleParameterCheckbox = () => {
    if (parameterChecked) {
      setParameterChecked(false);
      setEndDateChecked(false);
    } else {
      setParameterChecked(true);
    }
  };

  return (
    <Container>
      <Checkbox
        key={parameterValue}
        id={parameterValue}
        checked={parameterChecked}
        onChange={toggleParameterCheckbox}
        disabled={disabled}
      >
        {t(getParameterTKeys(parameterValue).name)}
      </Checkbox>
      {parameterChecked && (
        <Container style={{ marginLeft: 30, marginTop: 10 }}>
          <Feedback type="warning" size="slim" marginTop={10} marginBottom={10}>
            {t('form.parameter.feedbackText')}
          </Feedback>
          <Text marginBottom={10} color="black70">
            {t('form.parameter.validFromText')}
          </Text>
          <Checkbox
            key={parameterValue + '-pension-parameter-end-date-confirmation'}
            id={parameterValue + '-pension-parameter-end-date-confirmation'}
            checked={endDateChecked}
            onChange={() => setEndDateChecked(!endDateChecked)}
            disabled={disabled}
          >
            {t(getParameterTKeys(parameterValue).description)}
            {t('form.parameter.setEndDate')}
          </Checkbox>
          {endDateChecked && <ParameterDateInputFields endDateInputProps={endDateInput.props} disabled={disabled} />}
        </Container>
      )}
    </Container>
  );
};
